import { Button } from '@/components/ui/button'
import { CheckCircleFillIcon, XCloseIcon } from '@/components/ui/icons'
import { cn } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'

interface CheckInToastProps {
	/** Confirmation line, e.g. "Tom Becker checked-in". */
	title: string
	/** Whether react-hot-toast has this toast in its visible (entered) state. */
	visible: boolean
	/** Reverts the check-in (calls the undo mutation) and dismisses the toast. */
	onUndo: () => void
	/** Dismisses the toast without touching the check-in. */
	onDismiss: () => void
}

/**
 * Confirmation toast shown after a day-of check-in, carrying an inline "Undo"
 * action (POST /check-in/undo). Rendered via `toast.custom` — matches the DLS
 * toast layout: green check + title/description, a dismiss X, and a full-width
 * Undo button beneath.
 */
export const CheckInToast = ({
	title,
	visible,
	onUndo,
	onDismiss
}: CheckInToastProps) => {
	const { t } = useTranslation()

	return (
		<div
			className={cn(
				'flex w-[360px] max-w-[calc(100vw-2rem)] flex-col gap-4 rounded-2xl bg-neutral-white p-4 shadow-airbnb-4 transition-all duration-200',
				visible
					? 'translate-y-0 opacity-100'
					: 'translate-y-2 opacity-0'
			)}
		>
			<div className='flex items-start gap-3'>
				<div className='flex flex-1 flex-col gap-1'>
					<div className='flex items-center gap-3'>
						<CheckCircleFillIcon
							className='shrink-0 text-green-600'
							size={20}
						/>
						<p className='font-medium text-base text-grey-1000'>
							{title}
						</p>
					</div>
				</div>
				<button
					aria-label={t('marquee:checkin_toast_dismiss')}
					className='shrink-0 rounded-full p-1 text-grey-1000 hover:bg-grey-200'
					onClick={onDismiss}
					type='button'
				>
					<XCloseIcon size={16} />
				</button>
			</div>
			<Button
				className='w-full'
				onClick={onUndo}
				size='md'
				type='button'
				variant='secondary'
			>
				{t('marquee:checkin_toast_undo')}
			</Button>
		</div>
	)
}

CheckInToast.displayName = 'CheckInToast'
